import { LessonSegment, SegmentMedia } from 'controllers/types';
import { useKyronMutation } from './kyronMutation';
import { useKyronQuery } from './kyronQuery';

export const useSegmentQuery = (segmentId?: number) =>
  useKyronQuery<LessonSegment>(`/lesson_segments/${segmentId}`, {
    enabled: !!segmentId,
  });

export const useUpdateSegment = ({
  lessonId,
  segmentId,
  disableInvalidation,
}: {
  lessonId: number;
  segmentId: number;
  /**
   * We need disableInvalidation on this custom hook because this hook is being used with other mutations
   * and at some places we don't want to invalidate the data before other mutations are done. Otherwise, UI
   * flickers with obsolete data before the new data is fetched.
   */
  disableInvalidation?: boolean;
}) =>
  useKyronMutation<LessonSegment, LessonSegment>(`/lesson_segments/${segmentId}`, {
    method: 'PUT',
    invalidates: disableInvalidation ? undefined : [`/lessons/${lessonId}/lesson_sections`],
  });

export const useUpdateEdgeOfSegment = ({ lessonId, segmentId }: { lessonId: number; segmentId: number }) =>
  useKyronMutation<
    { edge_position: number; answer: string; correctness_status: string; description: string },
    LessonSegment
  >(`/lesson_segments/${segmentId}/update_edge`, {
    method: 'PUT',
    invalidatesMultiple: [[`/lessons/${lessonId}/lesson_segments`], [`/lessons/${lessonId}/lesson_sections`]],
  });

export const useGenerateSegmentVideo = ({ lessonId, segmentId }: { lessonId: number; segmentId: number }) =>
  useKyronMutation<{ text?: string; mediaType: SegmentMedia }, LessonSegment>(
    `/lesson_segments/${segmentId}/generate_video`,
    {
      method: 'POST',
      invalidatesMultiple: [[`/lessons/${lessonId}/lesson_segments`], [`/lessons/${lessonId}/lesson_sections`]],
    },
  );

export const useUpdateSegmentImage = ({ lessonId, segmentId }: { lessonId: number; segmentId: number }) =>
  useKyronMutation<{ image_signed_id: string }, LessonSegment>(
    `/lesson_segments/${segmentId}/update_image_and_regenerate`,
    {
      method: 'PUT',
      invalidatesMultiple: [[`/lessons/${lessonId}/lesson_segments`], [`/lessons/${lessonId}/lesson_sections`]],
    },
  );
